import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, m, useReducedMotion } from 'motion/react'
import { useKeys } from 'services/KeysProvider'
import { formatPrice } from '../../../../utils/utils'
import type { TilesCartItem } from 'stores/cartStore/CartItem'
import type { GroupPricingProduct } from '../../groupCheckoutPricing.types'
import { buildFrameBreakdownItems, splitLabel } from '../../sizeBreakdownItems'
import Skeleton from 'components/Skeleton/Skeleton'
import * as S from './SizeBreakdown.styles'
import { CART_ENTER_TRANSITION, CART_EXIT_TRANSITION } from './consts'

const INSTANT_TRANSITION = { duration: 0 } as const

type SizeBreakdownProps = {
  item: TilesCartItem
  pricingProducts?: GroupPricingProduct[]
  currency?: string
  isExpanded: boolean
  isPricingLoading?: boolean
}

function SizeBreakdown({
  item,
  pricingProducts,
  currency,
  isExpanded,
  isPricingLoading,
}: SizeBreakdownProps) {
  const { t } = useTranslation()
  const { ipCountry } = useKeys()
  const shouldReduceMotion = useReducedMotion()

  const items = useMemo(
    () => buildFrameBreakdownItems(item, t, ipCountry, pricingProducts),
    [item, t, ipCountry, pricingProducts]
  )

  const enterTransition = shouldReduceMotion
    ? INSTANT_TRANSITION
    : CART_ENTER_TRANSITION
  const exitTransition = shouldReduceMotion
    ? INSTANT_TRANSITION
    : CART_EXIT_TRANSITION

  return (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <m.div
          key="breakdown"
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: 'auto',
            opacity: 1,
            transition: enterTransition,
          }}
          exit={{ height: 0, opacity: 0, transition: exitTransition }}
          style={{ overflow: 'hidden' }}
          data-testid="cart-size-breakdown"
          data-expanded={isExpanded.toString()}
        >
          <S.BreakdownList>
            <S.BreakdownListInner>
              {items.map((breakdownItem) => {
                const hasDiscount =
                  breakdownItem.priceInfo &&
                  breakdownItem.priceInfo.originalPrice !==
                    breakdownItem.priceInfo.discountedPrice
                const { parts } = splitLabel(breakdownItem.label)
                return (
                  <S.BreakdownRow
                    key={breakdownItem.label}
                    data-testid="cart-breakdown-row"
                    data-size={breakdownItem.size}
                    data-frame={breakdownItem.frame}
                  >
                    <S.Label>
                      {parts.map((part) =>
                        part.light ? (
                          <S.LabelSize key={part.text}>{part.text}</S.LabelSize>
                        ) : (
                          <S.LabelCount key={part.text}>
                            {part.text}
                          </S.LabelCount>
                        )
                      )}
                    </S.Label>
                    {isPricingLoading ? (
                      <S.PriceGroup>
                        <Skeleton width={40} />
                      </S.PriceGroup>
                    ) : (
                      currency &&
                      breakdownItem.priceInfo && (
                        <S.PriceGroup>
                          <S.DiscountedPrice data-testid="cart-breakdown-price">
                            {formatPrice(
                              breakdownItem.priceInfo.discountedPrice,
                              currency
                            )}
                          </S.DiscountedPrice>
                          {hasDiscount && (
                            <S.OriginalPrice data-testid="cart-breakdown-original-price">
                              {formatPrice(
                                breakdownItem.priceInfo.originalPrice,
                                currency
                              )}
                            </S.OriginalPrice>
                          )}
                        </S.PriceGroup>
                      )
                    )}
                  </S.BreakdownRow>
                )
              })}
            </S.BreakdownListInner>
          </S.BreakdownList>
        </m.div>
      )}
    </AnimatePresence>
  )
}

export default SizeBreakdown
